/**
 * Cookie 操作工具类
 */
export class CookieUtils {
  /**
   * 设置 Cookie
   * @param name Cookie 名称
   * @param value Cookie 值
   * @param options Cookie 选项
   */
  static set(
    name: string,
    value: string | number | boolean,
    options: {
      expires?: number | Date;
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {},
  ): void {
    const { expires, path = '/', domain, secure, sameSite } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`;

    // 设置过期时间
    if (expires) {
      if (typeof expires === 'number') {
        const date = new Date();
        date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
        cookieString += `; expires=${date.toUTCString()}`;
      } else {
        cookieString += `; expires=${expires.toUTCString()}`;
      }
    }

    // 设置路径
    cookieString += `; path=${path}`;

    // 设置域名
    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    // 设置安全标志
    if (secure) {
      cookieString += '; secure';
    }

    // 设置 SameSite
    if (sameSite) {
      cookieString += `; samesite=${sameSite}`;
    }

    document.cookie = cookieString;
  }

  /**
   * 获取 Cookie 值
   * @param name Cookie 名称
   * @returns Cookie 值，如果不存在则返回 null
   */
  static get(name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }

    return null;
  }

  /**
   * 删除 Cookie
   * @param name Cookie 名称
   * @param options Cookie 选项（路径、域名等）
   */
  static delete(
    name: string,
    options: {
      path?: string;
      domain?: string;
    } = {},
  ): void {
    const { path, domain } = options;

    // 设置过期时间为过去的时间来删除 Cookie
    this.set(name, '', {
      expires: -1,
      path,
      domain,
    });
  }

  /**
   * 检查 Cookie 是否存在
   * @param name Cookie 名称
   * @returns 是否存在
   */
  static has(name: string): boolean {
    return this.get(name) !== null;
  }

  /**
   * 获取所有 Cookie
   * @returns 所有 Cookie 的键值对对象
   */
  static getAll(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }

      const cookieParts = c.split('=');
      if (cookieParts.length >= 2) {
        const name = decodeURIComponent(cookieParts[0]);
        const value = decodeURIComponent(cookieParts.slice(1).join('='));
        cookies[name] = value;
      }
    }

    return cookies;
  }
}

// 为了兼容旧的使用方式，提供简化的函数式接口
export const setCookie = CookieUtils.set;
export const getCookie = CookieUtils.get;
export const deleteCookie = CookieUtils.delete;
export const hasCookie = CookieUtils.has;
export const getAllCookies = CookieUtils.getAll;
